import React from 'react';

import { Color, GameVersion, Quality } from 'common';
import Data from 'data';
import { isSubType } from 'logic/itemTypes';
import { monsterBossList } from 'logic/monster/monsterList';
import { underlinerFunc } from 'ui/select';
import { D2ItemTooltip } from 'tooltips';

export const renderOption = (opt, search) => <span className={`d2-color-${opt.color || 0}`}>{search ? underlinerFunc(opt.name, search) : opt.name}</span>;
export const renderItemOption = (opt, search) => <D2ItemTooltip base={opt.id}>{renderOption(opt, search)}</D2ItemTooltip>;

let _uniqueRemap = null;
export function uniquesByName() {
  if (!_uniqueRemap) {
    _uniqueRemap = {};
    for (const id in Data.uniqueItems) {
      _uniqueRemap[Data.uniqueItems[id].index] = id;
    }
    for (const id in Data.setItems) {
      _uniqueRemap[Data.setItems[id].index] = id;
    }
  }
  return _uniqueRemap;
}

export function updateTC(data, playerFactor) {
  const {entryData, entryDataF, tcList, tcMap, itemList} = data;
  const uniqueRemap = uniquesByName();

  for (const tc of tcList) {
    if (!tc.drops) continue;
    const {noDrop, probSum, picks} = tc;
    const ndN = Math.pow(noDrop, playerFactor);
    const dropD = probSum + Math.floor(ndN * probSum / (Math.pow(noDrop + probSum, playerFactor) - ndN));
    tc.dropD = dropD;

    if (tc.picks > 0 && tc.drops.length === 2 && tcList[tcMap[tc.drops[1][0]]]?.picks > 1) {
      // Duriel special case
      
      let sum1 = 0, sum2 = 0;
      const tc2 = tcList[tcMap[tc.drops[1][0]]];
      const p2 = tc2.probSum / tc2.dropD;
      function recursive(index, prob, drops, num1, num2) {
        if (index >= tc.picks || drops >= 6) {
          sum1 += prob * num1;
          sum2 += prob * num2;
        } else {
          recursive(index + 1, prob * tc.drops[0][1] / tc.probSum, drops + 1, num1 + 1, num2);
          let cnk = 1;
          const prob2 = prob * tc.drops[1][1] / tc.probSum;
          for (let i = 0; i <= tc2.picks; ++i) {
            const add = Math.min(i, 6 - drops);
            recursive(index + 1, prob2 * cnk * Math.pow(p2, i) * Math.pow(1 - p2, tc2.picks - i), drops + add, num1, num2 + add);
            cnk *= (tc2.picks - i) / (i + 1);
          }
        }
      }
      recursive(0, 1, 0, 0, 0);

      const tc0 = tc.entries[0];
      if (itemList[entryData[tc0 * 6]].id !== tc.drops[0][0]) throw Error('wrong item');
      entryDataF[tc0 * 2] = sum1;
      entryDataF[tc0 * 2 + 1] = 1;

      const multN = sum2 / tc2.realPicks;
      const multD = tc2.probSum / tc2.dropD;

      let pos = 1;
      for (const entry of tc2.entries) {
        const tce = tc.entries[pos++];
        if (entryData[tce * 6] !== entryData[entry * 6]) throw Error('wrong item');
        entryDataF[tce * 2] = multN * entryDataF[entry * 2];
        entryDataF[tce * 2 + 1] = multD * entryDataF[entry * 2 + 1];
      }

      continue;
    }

    if (tc.picks === -2 && tc.drops.length === 2 && tcList[tcMap[tc.drops[0][0]]]?.picks > 1) {
      // Countess special case

      let sum2 = 0;
      const tc1 = tcList[tcMap[tc.drops[0][0]]];
      const tc2 = tcList[tcMap[tc.drops[1][0]]];
      const p1 = tc1.probSum / tc1.dropD;
      const p2 = tc2.probSum / tc2.dropD;
      let cnk1 = 1;
      for (let i = 0; i <= tc1.picks; ++i) {
        const prob = cnk1 * Math.pow(p1, i) * Math.pow(1 - p1, tc1.picks - i);
        cnk1 *= (tc1.picks - i) / (i + 1);

        const remain = 6 - i;
        let rp = tc2.picks;
        if (rp > remain) {
          let cnk2 = 1;
          for (let j = tc2.picks; j > remain; --j) {
            rp -= (j - remain) * cnk2 * Math.pow(p2, j - 1) * Math.pow(1 - p2, tc2.picks - j);
            cnk2 *= j / (tc2.picks - j + 1);
          }
        }
        sum2 += prob * rp;
      }

      let pos = 0;
      for (const entry of tc1.entries) {
        const tce = tc.entries[pos++];
        if (entryData[tce * 6] !== entryData[entry * 6]) throw Error('wrong item');
        entryDataF[tce * 2] = entryDataF[entry * 2];
        entryDataF[tce * 2 + 1] = entryDataF[entry * 2 + 1];
      }

      const factor = sum2 / tc2.realPicks;

      for (const entry of tc2.entries) {
        const tce = tc.entries[pos++];
        if (entryData[tce * 6] !== entryData[entry * 6]) throw Error('wrong item');
        entryDataF[tce * 2] = factor * entryDataF[entry * 2];
        entryDataF[tce * 2 + 1] = entryDataF[entry * 2 + 1];
      }

      continue;
    }
    
    tc.dropD = dropD;
    tc.realPicks = picks;
    if (picks > 6) {
      let cnk = 1;
      const prob = probSum / dropD;
      for (let i = picks; i > 6; --i) {
        tc.realPicks -= (i - 6) * cnk * Math.pow(prob, i - 1) * Math.pow(1 - prob, picks - i);
        cnk *= i / (picks - i + 1);
      }
    }

    let pos = 0;
    let npicks = -picks;
    for (const [id, prob] of tc.drops) {
      let chanceN = tc.realPicks * prob;
      if (picks < 0) {
        if (npicks <= 0) break;
        chanceN = Math.min(npicks, prob);
        npicks -= chanceN;
      }
      const chanceD = (picks >= 0 ? dropD : 1);

      if (Data.items[id] || uniqueRemap[id] || id.match(/^gld(?:,mul=\d+)?$/)) {
        const tce = tc.entries[pos++];
        if (itemList[entryData[tce * 6]].id !== (uniqueRemap[id] || id)) throw Error('wrong item');
        entryDataF[tce * 2] = chanceN;
        entryDataF[tce * 2 + 1] = chanceD;
      } else if (tcMap[id] != null) {
        for (const entry of tcList[tcMap[id]].entries) {
          const tce = tc.entries[pos++];
          if (entryData[tce * 6] !== entryData[entry * 6]) throw Error('wrong item');
          entryDataF[tce * 2] = chanceN * entryDataF[entry * 2];
          entryDataF[tce * 2 + 1] = chanceD * entryDataF[entry * 2 + 1];
        }
      }
    }
    if (pos !== tc.entries.length) throw Error('length mismatch');
  }
  return {...data};
}

export function calculateTC(playerFactor=1) {
  const entryData = new Uint32Array(1572864 * 6);
  const entryDataF = new Float64Array(1572864 * 2);
  const tcList = [];
  const itemList = [];
  const tcMap = {};
  const itemMap = {};

  function itemIndex(id) {
    if (itemMap[id] == null) {
      itemMap[id] = itemList.length;
      itemList.push({id, entries: []});
    }
    return itemMap[id];
  }
  function tcIndex(id) {
    if (tcMap[id] == null) {
      tcMap[id] = tcList.length;
      tcList.push({id, entries: []});
    }
    return tcMap[id];
  }

  let entryPos = 0;
  function makeEntry(item, tc, chanceN, chanceD, unique=0, set=0, rare=0, magic=0) {
    const pos = entryPos;
    entryData.set([item, tc, unique, set, rare, magic], pos * 6);
    entryDataF.set([chanceN, chanceD], pos * 2);
    itemList[item].entries.push(pos);
    tcList[tc].entries.push(pos);
    entryPos += 1;
    return pos;
  }

  for (const type in Data.itemTypes) {
    if (!Data.itemTypes[type].treasureclass) continue;
    const types = new Set(Object.keys(Data.itemTypes).filter(t => isSubType(t, type)));

    for (let index = 3; index <= 99; index += 3) {
      tcIndex(type + index);
    }

    for (const [id, item] of Object.entries(Data.items)) {
      if (!item.spawnable || item.quest || !item.level) continue;
      if (!types.has(item.type) && !types.has(item.type2)) continue;

      const iid = itemIndex(id);
      const tid = tcIndex(`${type}${Math.ceil(item.level / 3) * 3}`);

      makeEntry(iid, tid, 1, 1);
    }
  }
  for (const tc of tcList) {
    for (const entry of tc.entries) {
      entryDataF[entry * 2 + 1] = tc.entries.length;
    }
  }

  const uniqueRemap = uniquesByName();

  const groupLast = {};
  for (const [tc, info] of Object.entries(Data.treasureClass)) {
    const {group, level, picks=0, unique=0, set=0, rare=0, magic=0, nodrop=0} = info;

    const tid = tcIndex(tc);
    const res = tcList[tid];
    res.level = level;
    if (group) {
      if (groupLast[group]) {
        groupLast[group].next = res;
        res.prev = groupLast[group];
      }
      groupLast[group] = res;
    }

    const drops = [...Array(10)].map((_, idx) => [info[`item${idx + 1}`], info[`prob${idx + 1}`]]).filter(drop => drop[0] && drop[1]);
    const probSum = drops.reduce((sum, drop) => sum + drop[1], 0);
    res.picks = picks;
    res.probSum = probSum;
    res.noDrop = nodrop;
    res.drops = drops;

    let npicks = -picks;
    for (const [id, prob] of drops) {
      if (picks < 0) {
        if (npicks <= 0) break;
        npicks -= prob;
      }

      if (Data.items[id] || uniqueRemap[id] || id.match(/^gld(?:,mul=\d+)?$/)) {
        makeEntry(itemIndex(uniqueRemap[id] || id), tid, 0, 0, unique, set, rare, magic);
      } else if (tcMap[id] != null) {
        for (const entry of tcList[tcMap[id]].entries) {
          const e6 = entry * 6;
          makeEntry(entryData[e6], tid, 0, 0,
            Math.max(unique, entryData[e6 + 2]),
            Math.max(set, entryData[e6 + 3]),
            Math.max(rare, entryData[e6 + 4]),
            Math.max(magic, entryData[e6 + 5]));
        }
      } else {
        console.error(`Unknown item: ${id}`);
      }
    }
  }

  return updateTC({entryData, entryDataF, tcList, itemList, tcMap, itemMap}, playerFactor);
}

export function formatRatio(chance) {
  if (!chance) return '0';
  if (chance > 1) {
    return (chance * 100).toFixed(0) + '%';
  }
  chance = 1 / chance;
  const digits = (chance < 10 ? 2 : (chance < 100 ? 1 : 0));
  chance = parseFloat(chance.toFixed(digits)).toString();
  const parts = chance.split(".");
  if (parseFloat(chance) >= 10000) {
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }
  return '1:' + parts.join('.');
}
export const formatPercent = chance => chance ? (chance * 100).toFixed(1) + '%' : '-';

export function itemRatio(id) {
  const item = Data.items[id];
  const uber = (item.code === item.ubercode || item.code === item.ultracode);
  const classspecific = Data.itemTypes[item.type]?.class;
  return Data.itemRatio.find(row => !row.version === (GameVersion !== 100) && !row.uber === !uber && !row.classspecific === !classspecific) || Data.itemRatio[2];
}

export function qualityChance(quality, base, divisor, min, qfactor, ilvl, qlvl, mf) {
  let chance = (base - (((ilvl - qlvl) / divisor) | 0)) * 128;
  if (quality >= Quality.MAGIC) {
    if (quality >= Quality.RARE && mf > 10) {
      const factor = [600, 500, 250][quality - Quality.RARE];
      mf = (mf * factor / (mf + factor)) | 0;
    }
    chance = (chance * 100 / (100 + mf)) | 0;
  }
  if (chance < min) chance = min;
  chance -= (chance * qfactor / 1024) | 0;
  return 128 / Math.max(128, chance);
}

export function monsterTC(monster, level, difficulty, tcData) {
  if (monster.type === 'chest') {
    const level = Data.levels[monster.area];
    if (!level) return null;
    const actLow = [2, 41, 76, 104, 109][level.act || 0];
    const actHigh = [37, 73, 102, 108, 132][level.act || 0];
    const sep = Math.floor((actHigh - actLow + 1) / 3);
    let tc = `Act ${(level.act || 0) + 1}`;
    if (difficulty === 1) tc += ' (N)';
    if (difficulty === 2) tc += ' (H)';
    tc += ' Chest ';
    if (level.id < actLow + sep) tc += 'A';
    else if (level.id < actLow + sep * 2) tc += 'B';
    else tc += 'C';
    return tcData.tcList[tcData.tcMap[tc]];
  }

  const monId = (monster.type === 'super' ? Data.superUniques[monster.id]?.class : monster.id);
  const monInfo = Data.monsters[monId];
  if (!monInfo) return null;

  const suffix = ['', 'n', 'h'][difficulty];

  let tc;
  if (Data.info?.patch) {
    if (monster.type === 'champion') {
      tc = (monster.terrorized && monInfo[`treasureclassdesecratedchamp${suffix}`]) || monInfo[`treasureclasschamp${suffix}`];
    } else if (monster.type === 'unique' || monster.type === 'super' || monster.type === 'boss') {
      tc = (monster.terrorized && monInfo[`treasureclassdesecratedunique${suffix}`]) || monInfo[`treasureclassunique${suffix}`];
      if (monster.type === 'boss' && monster.quest) {
        tc = monInfo[`treasureclassquest${suffix}`] || tc;
      }
    } else {
      tc = (monster.terrorized && monInfo[`treasureclassdesecrated${suffix}`]) || monInfo[`treasureclass${suffix}`];
    }
  } else {
    let index = 1;
    if (monster.type === 'champion') {
      index = 2;
    } else if (monster.type === 'unique' || monster.type === 'super' || monster.type === 'boss') {
      index = 3;
      if (monster.type === 'boss' && monInfo[`treasureclass4${suffix}`] && monster.quest) {
        index = 4;
      }
    }
    tc = monInfo[`treasureclass${index}${suffix}`];
  }

  if (monster.type === 'super') {
    tc = Data.superUniques[monster.id]['tc' + suffix];
  } else if (monster.type === 'boss') {
    const unique = monsterBossList[monster.id]?.unique;
    if (unique) {
      tc = Data.superUniques[unique]['tc' + suffix];
    }
  }

  if (!tc || tcData.tcMap[tc] == null) return null;

  let entry = tcData.tcList[tcData.tcMap[tc]];

  if (monster.type !== 'super' && monster.type !== 'boss') {
    if (difficulty > 0) {
      while (entry.next && entry.next.level <= level) {
        entry = entry.next;
      }
    }
  }

  return entry;
}

export function qualityProb(dst, prob, id, ratio, uniqueFactor, setFactor, rareFactor, magicFactor, level, mf) {
  const item = Data.items[id];
  if (!item) return;
  const type = Data.itemTypes[item.type];
  if (!type || type.normal) return;
  if (!type.normal) {
    let remain = 1;

    const unique = qualityChance(Quality.UNIQUE, ratio.unique, ratio.uniquedivisor, ratio.uniquemin, uniqueFactor, level, item.level, mf);
    dst[Quality.UNIQUE] += unique * prob;
    remain -= unique;
    
    const set = remain * qualityChance(Quality.SET, ratio.set, ratio.setdivisor, ratio.setmin, setFactor, level, item.level, mf);
    dst[Quality.SET] += set * prob;
    remain -= set;
    
    if (type.rare) {
      const rare = remain * qualityChance(Quality.RARE, ratio.rare, ratio.raredivisor, ratio.raremin, rareFactor, level, item.level, mf);
      dst[Quality.RARE] += rare * prob;
      remain -= rare;
    }
    
    if (type.magic) {
      dst[Quality.MAGIC] += remain * prob;
    } else {
      const magic = remain * qualityChance(Quality.MAGIC, ratio.magic, ratio.magicdivisor, ratio.magicmin, magicFactor, level, item.level, mf);
      dst[Quality.MAGIC] += magic * prob;
      remain -= magic;
      
      const high = remain * qualityChance(Quality.SUPERIOR, ratio.hiquality, ratio.hiqualitydivisor, 0, 0, level, item.level, mf);
      dst[Quality.SUPERIOR] += high * prob;
      remain -= high;
      
      const normal = remain * qualityChance(Quality.NORMAL, ratio.normal, ratio.normaldivisor, 0, 0, level, item.level, mf);
      dst[Quality.NORMAL] += normal * prob;
      remain -= normal;

      dst[Quality.LOW] += remain * prob;
    }
  }
}

export function defaultState() {
  return {
    settings: {difficulty: 2, players: 1, party: 1, magicfind: 0, level: 99},
    mode: 'monster',
    monster: {type: 'boss', area: '2', id: 'baalcrab', minion: false, champion: 'champion'},
    item: {type: 'unique'},
  };
}

export function serializeState(state) {
  const result = [];
  if (state.settings.difficulty !== 2) {
    result.push(`d${state.settings.difficulty}`);
  }
  if (state.settings.players !== 1 || state.settings.party !== 1) {
    result.push(`p${state.settings.players},${state.settings.party}`);
  }
  if (state.settings.magicfind !== 0) {
    result.push(`mf${state.settings.magicfind}`);
  }
  if (state.settings.terrorized) {
    result.push(`tz`);
  }
  if (state.settings.level !== 99) {
    result.push(`l${state.settings.level}`);
  }
  if (state.mode === 'monster') {
    const monster = [state.monster.type];
    if (state.monster.type !== 'boss' && state.monster.type !== 'super') {
      monster.push(state.monster.area);
    }
    if (state.monster.type !== 'chest') {
      monster.push(state.monster.id)
    }
    if (state.monster.type === 'boss') {
      if (state.monster.quest) {
        monster.push('quest');
      }
    } else if (state.monster.type === 'normal') {
      if (state.monster.minion) {
        monster.push('minion');
      }
    } else if (state.monster.type === 'champion') {
      if (state.monster.champion === 'berserk') {
        monster.push('berserk');
      }
    } else if (state.monster.type === 'chest') {
      if (state.monster.locked) {
        monster.push('locked');
      }
    }
    result.push(`monster=${monster.join(',')}`);
  } else if (state.mode === 'item') {
    if (!state.item.id) return;
    const item = [state.item.type, state.item.id];
    if (state.item.type === 'base' && state.item.quality >= 0) {
      item.push(state.item.quality);
    }
    result.push(`item=${item.join(',')}`);
  } else if (state.mode === 'tc') {
    if (!state.tc) return;
    result.push(`tc=${encodeURIComponent(state.tc)}`);
  } else if (state.mode === 'gamble') {
    if (!state.gamble) return;
    result.push(`gamble=${state.gamble}`);
  }
  return result.join(';');
}

export function parseState(text) {
  const state = defaultState();
  if (!text) return state;

  for (const cmd of text.split(';')) {
    let m;
    if ((m = cmd.match(/^d(\d)$/))) {
      state.settings.difficulty = parseInt(m[1]);
    } else if ((m = cmd.match(/^p(\d),(\d)$/))) {
      state.settings.players = parseInt(m[1]);
      state.settings.party = parseInt(m[2]);
    } else if ((m = cmd.match(/^mf(\d+)$/))) {
      state.settings.magicfind = parseInt(m[1]);
    } else if (cmd === 'tz') {
      state.settings.terrorized = true;
    } else if ((m = cmd.match(/^l(\d+)$/))) {
      state.settings.level = parseInt(m[1]);
    } else if ((m = cmd.match(/^monster=(.*)$/))) {
      const parts = m[1].split(',');
      if (parts.length >= 2) {
        state.mode = 'monster';
        state.monster.type = parts.shift();
        if (state.monster.type !== 'boss' && state.monster.type !== 'super') {
          state.monster.area = parts.shift();
        }
        if (state.monster.type !== 'chest') {
          state.monster.id = parts.shift();
        }
        if (parts[0] === 'quest') state.monster.quest = true;
        if (parts[0] === 'minion') state.monster.minion = true;
        if (parts[0] === 'berserk') state.monster.champion = 'berserk';
        if (parts[0] === 'locked') state.monster.locked = true;
      }
    } else if ((m = cmd.match(/^item=(.*)$/))) {
      const parts = m[1].split(',');
      if (parts.length >= 2) {
        state.mode = 'item';
        state.item.type = parts[0];
        state.item.id = parts[1];
        if (parts[2]) state.item.quality = parseInt(parts[2]);
      }
    } else if ((m = cmd.match(/^tc=(.*)$/))) {
      state.mode = 'tc';
      state.tc = decodeURIComponent(m[1]);
    } else if ((m = cmd.match(/^gamble=(.*)$/))) {
      state.mode = 'gamble';
      state.gamble = m[1];
    }
  }
  return state;
}

export const NavigateContext = React.createContext();
export const NavigateLink = React.memo(({ to, children, ...props }) => {
  const {state, onNavigate} = React.useContext(NavigateContext);
  const nextState = React.useMemo(() => typeof to === 'function' ? to(state) : {...state, ...to}, [to, state]);
  const hash = React.useMemo(() => serializeState(nextState), [nextState]);

  const onClick = React.useCallback(e => {
    if (!e.ctrlKey) {
      e.preventDefault();
      onNavigate(nextState);
    }
  }, [nextState, onNavigate]);

  if (!to) return <span {...props}>{children}</span>;

  return <a href={'#' + hash} onClick={onClick} {...props}>{children}</a>;
});

export function baseItemColor(item) {
  if (item.type === 'rune') return Color.ORANGE;
  if (item.quest) return Color.GOLD;
  if (item.type === 'ques') return Color.ORANGE;
  if (Data.itemTypes[item.type]?.magic) return Color.BLUE;
  return Color.WHITE;
}

export const bossTitle = {
  diabloclone: 'World',
  ubermephisto: 'Pandemonium',
  uberdiablo: 'Pandemonium',
  uberbaal: 'Pandemonium',
  uberandariel: 'Pandemonium',
  uberduriel: 'Pandemonium',
  uberizual: 'Pandemonium',
};
